import { FC } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Typography } from 'antd';
import { TbBuilding } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { workspaceService } from '@/services/database';
import VerifiedDestructiveActionModal from '@/shared/components/modals/VerifiedDestructiveActionModal';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { SetStateMethod } from '@/types';

interface DeleteWorkspaceModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const DeleteWorkspaceModal: FC<DeleteWorkspaceModalProps> = ({ open, setOpen }): JSX.Element => {
    const navigate = useNavigate();
    const { selectedUser: user } = useUserStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const emailAddress = user?.emailAddress ?? '';
    const workspaceName = workspace?.name || 'This Workspace';

    const handleDeleteWorkspace = async (verificationCode: string) => {
        if (!workspace?.id) {
            throw new Error('Select a workspace before deleting it.');
        }

        try {
            await workspaceService.deleteWithVerification(workspace.id, { acknowledged: true, verificationCode });
            localStorage.clear();
            navigate('/login');
        } catch (error) {
            captureException(error);
            throw error;
        }
    };

    return (
        <VerifiedDestructiveActionModal
            acknowledgementLabel={`I understand that ${workspaceName} and all of its data will be permanently deleted.`}
            actionLabel='Delete My Workspace'
            alertDescription='Deleting this workspace permanently removes its users, investments, teams, settings, and all other associated resources. Export any records you need before continuing.'
            emailAddress={emailAddress}
            errorMessage='Unable to delete workspace'
            onCancel={() => setOpen(false)}
            onConfirm={handleDeleteWorkspace}
            onRequestCode={() => {
                if (!workspace?.id) {
                    throw new Error('Select a workspace before requesting a deletion code.');
                }
                return workspaceService.requestDeletionVerification(workspace.id);
            }}
            open={open}
            title='Verify Workspace Deletion Request'
        >
            <div style={{ alignItems: 'center', display: 'flex', gap: '0.75rem' }}>
                <Avatar icon={workspace?.emoji || <TbBuilding />} shape='square' size={40} />
                <div style={{ minWidth: 0 }}>
                    <Typography.Text strong>{workspaceName}</Typography.Text>
                    <small style={{ display: 'block' }}>Deletion verification will be sent to {emailAddress}.</small>
                </div>
            </div>
        </VerifiedDestructiveActionModal>
    );
};

export default DeleteWorkspaceModal;
